import React, { ReactElement, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconNames, CUIColors } from '@dotcom/crown-ui';
import { useAppContext } from '../../../context/app-context';
import { useUIStore } from '../../../stores/ui';
import { FabricTextBox, BottomPadding } from '../../../global-types';
import { useActiveCanvas } from '../../../hooks';
import {
  captureCanvasSnapshot,
  useEditHistoryCoordinator,
  useEditHistorySessionKey,
  useHistoryTransaction,
  useHistoryTransactionErrorToast,
  useResolveEditHistoryFaceIdentity,
} from '../../../features/edit-history/edit-history';
import { getGroupedTextObject } from '../../../utils';
import { getOrderIndices } from '../../../utils/canvas-utils/get-order-indices';

import { Tabs } from '../../tabs/tabs';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import styles from './font-drawer.module.scss';
import { ColorTab, PositionTab, TextTab, FontTab, FontDrawerHeader, FontDrawerFooter } from './fragments';
import { useCloseFontDrawer } from './hooks/use-close-font-drawer';
import { useFontDrawerFlags } from './hooks/use-font-drawer-flags';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { handleTextTabClick } from './utils/handle-text-tab-click';
import { onRevertStyles } from './utils/on-revert-styles';

export const FontDrawer = (): ReactElement => {
  const canvas = useActiveCanvas();
  const resolveHistoryFace = useResolveEditHistoryFaceIdentity();
  const historyCoordinator = useEditHistoryCoordinator();
  const runHistoryTransaction = useHistoryTransaction();
  const editorSessionKey = useEditHistorySessionKey();
  const textTabRef = useRef<HTMLButtonElement>(null);
  const fontTabRef = useRef<HTMLButtonElement>(null);
  const colorTabRef = useRef<HTMLButtonElement>(null);
  const positionTabRef = useRef<HTMLButtonElement>(null);
  const currentText = canvas?.current?.getActiveObject() as FabricTextBox;
  const { t } = useTranslation();
  const { appState } = useAppContext();
  const showHistoryCommitError = useHistoryTransactionErrorToast();
  const { shouldShowColorEditor, shouldShowPositionTabEditor } = useFontDrawerFlags();
  const { IS_MOBILE_COLOR_SCROLL_ENABLED } = useFeatureFlags();
  const selectedTextInitialState = Object.assign({}, getGroupedTextObject(canvas?.current));
  const isTextDrawerOpen = useUIStore((state) => state.isTextDrawerOpen);
  const { isMobileApp, isTabletApp } = appState;
  const shouldShowFontToolbox = currentText?.data.displayFontToolbox ?? true;
  const { onFontDrawerSubmit, onFontDrawerClose } = useCloseFontDrawer();
  const activeCanvas = canvas?.current;
  const historyFace = useMemo(
    () => (activeCanvas ? resolveHistoryFace?.(activeCanvas) : null),
    [activeCanvas, resolveHistoryFace],
  );
  const historySnapshotRef = useRef<ReturnType<typeof captureCanvasSnapshot> | null>(null);

  useEffect(() => {
    if (!isTextDrawerOpen || !historyCoordinator || !editorSessionKey || !historyFace || !canvas?.current) {
      historySnapshotRef.current = null;
      return;
    }
    try {
      const targetCanvas = canvas.current;
      const beforeSnapshot = captureCanvasSnapshot(targetCanvas);
      historySnapshotRef.current = beforeSnapshot;
      const unregisterPending = historyCoordinator.registerPendingTransaction(
        { editorSessionKey, faceId: historyFace.faceId, faceNumber: historyFace.faceNumber, type: historyFace.type },
        targetCanvas,
        {
          hasPendingChanges: () => false,
          getBeforeSnapshot: () => historySnapshotRef.current,
          rebase: (snapshot) => {
            historySnapshotRef.current = snapshot;
          },
        },
      );
      return () => {
        unregisterPending();
        if (historySnapshotRef.current !== beforeSnapshot) {
          return;
        }
        const currentFace = resolveHistoryFace?.(targetCanvas);
        if (
          canvas?.current !== targetCanvas ||
          currentFace?.editorSessionKey !== editorSessionKey ||
          currentFace?.faceId !== historyFace.faceId ||
          currentFace?.faceNumber !== historyFace.faceNumber ||
          currentFace?.type !== historyFace.type
        ) {
          historySnapshotRef.current = null;
          return;
        }
        historySnapshotRef.current = null;
        void historyCoordinator
          .commitCapturedTransaction(
            {
              editorSessionKey,
              faceId: historyFace.faceId,
              faceNumber: historyFace.faceNumber,
              type: historyFace.type,
            },
            targetCanvas,
            beforeSnapshot,
          )
          .catch(showHistoryCommitError);
      };
    } catch {
      historySnapshotRef.current = null;
    }
  }, [
    isTextDrawerOpen,
    historyCoordinator,
    editorSessionKey,
    historyFace,
    canvas,
    resolveHistoryFace,
    showHistoryCommitError,
  ]);

  const commitFontDrawerHistory = async (): Promise<void> => {
    if (!historySnapshotRef.current || !historyCoordinator || !editorSessionKey || !historyFace || !canvas?.current) {
      historySnapshotRef.current = null;
      return;
    }

    const targetCanvas = canvas.current;
    const currentFace = resolveHistoryFace?.(targetCanvas);
    if (
      currentFace?.editorSessionKey !== editorSessionKey ||
      currentFace?.faceId !== historyFace.faceId ||
      currentFace?.faceNumber !== historyFace.faceNumber ||
      currentFace?.type !== historyFace.type
    ) {
      historySnapshotRef.current = null;
      return;
    }

    const beforeSnapshot = historySnapshotRef.current;
    historySnapshotRef.current = null;
    await historyCoordinator.commitCapturedTransaction(
      {
        editorSessionKey,
        faceId: historyFace.faceId,
        faceNumber: historyFace.faceNumber,
        type: historyFace.type,
      },
      targetCanvas,
      beforeSnapshot,
    );
  };

  const handleFontDrawerClose = () => {
    if (!historySnapshotRef.current || !historyCoordinator || !editorSessionKey || !historyFace || !canvas?.current) {
      onFontDrawerClose(canvas);
      return;
    }
    void commitFontDrawerHistory()
      .catch(showHistoryCommitError)
      .finally(() => onFontDrawerClose(canvas));
  };

  const handleFontDrawerSubmit = () => {
    if (!historySnapshotRef.current || !historyCoordinator || !editorSessionKey || !historyFace || !canvas?.current) {
      onFontDrawerSubmit(canvas);
      return;
    }
    void commitFontDrawerHistory()
      .catch(showHistoryCommitError)
      .finally(() => onFontDrawerSubmit(canvas));
  };

  const handleFontDrawerCancel = () => {
    historySnapshotRef.current = null;
    const revertStyles = () =>
      onRevertStyles(selectedTextInitialState, canvas, originalObjectsOrder, () => onFontDrawerClose(canvas));
    if (canvas?.current) {
      void runHistoryTransaction(canvas.current, revertStyles);
    } else {
      revertStyles();
    }
  };

  // save order of every object in the canvas to revert on cancel
  const originalObjectsOrder = useMemo(() => {
    const { objects: originalObjectsOrder } = getOrderIndices(canvas?.current);
    return originalObjectsOrder;
  }, [canvas?.current]);

  const tabsDataBase = [
    {
      tabLabel: t('fontDrawer.textTabLabel'),
      tabContent: <TextTab />,
      tabIcon: IconNames.Keyboard,
      buttonRef: textTabRef,
    },

    { tabLabel: t('fontDrawer.FontTabLabel'), tabContent: <FontTab />, tabIcon: IconNames.Font, buttonRef: fontTabRef },
  ];

  if (shouldShowColorEditor) {
    tabsDataBase.push({
      tabLabel: t('fontDrawer.ColorTabLabel'),
      tabContent: <ColorTab scrollable={isMobileApp === true && IS_MOBILE_COLOR_SCROLL_ENABLED} />,
      tabIcon: IconNames.Paint,
      buttonRef: colorTabRef,
    });
  }

  if (shouldShowPositionTabEditor) {
    tabsDataBase.push({
      tabLabel: t('fontDrawer.PositionTabLabel'),
      tabContent: <PositionTab />,
      tabIcon: IconNames.Object,
      buttonRef: positionTabRef,
    });
  }

  useEffect(() => {
    const button = textTabRef.current;
    if (button && canvas?.current && isMobileApp) {
      button.addEventListener('click', () => handleTextTabClick(canvas?.current, isMobileApp));
    }

    return () => {
      if (button && canvas?.current && isMobileApp) {
        button.removeEventListener('click', () => handleTextTabClick(canvas?.current, isMobileApp));
      }
    };
  }, []);

  return (
    <Drawer
      addClass={styles['font-drawer']}
      drawerPosition={isMobileApp ? DrawerPositions.Bottom : DrawerPositions.Left}
      isOpen={isTextDrawerOpen && shouldShowFontToolbox}
      onClose={handleFontDrawerClose}
      headerContent={<FontDrawerHeader />}
      footerContent={<FontDrawerFooter onSubmit={handleFontDrawerSubmit} onCancel={handleFontDrawerCancel} />}
      id="font_drawer"
      showVeil={false}
      closeButtonText="Close Drawer"
      drawerTestId="font-drawer"
    >
      <Tabs
        addClass={styles['font-drawer-tabs']}
        breakTabs={isMobileApp || isTabletApp ? false : true}
        tabImageColor={CUIColors.Blue}
        tabsItems={tabsDataBase}
        backgroundColor={'white'}
        bottomPadding={BottomPadding.Small}
      />
    </Drawer>
  );
};
